import { useState, useEffect, useCallback } from 'react'
import { geolocationService } from '@/services/geolocation'
import type { GeolocationData } from '@/types'

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean
  timeout?: number
  maximumAge?: number
}

export function useGeolocation(options: UseGeolocationOptions = {}) {
  const [position, setPosition] = useState<GeolocationPosition | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accuracy, setAccuracy] = useState<number | null>(null)

  const getCurrentPosition = useCallback(async (customOptions?: UseGeolocationOptions) => {
    setLoading(true)
    setError(null)

    const finalOptions = { ...options, ...customOptions }

    try {
      const position = await geolocationService.getCurrentPosition(finalOptions)
      setPosition(position)
      setAccuracy(position.coords.accuracy)
      return position
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao obter localização'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [options])

  const getCurrentPositionHighAccuracy = useCallback(async () => {
    return getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0, // Sempre obter nova localização
    })
  }, [getCurrentPosition])

  const getCurrentPositionFast = useCallback(async () => {
    return getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 8000,
      maximumAge: 0, // Sempre obter nova localização
    })
  }, [getCurrentPosition])

  const watchPosition = useCallback(() => {
    setError(null)
    
    const watchId = geolocationService.watchPosition(
      (position) => {
        setPosition(position)
      },
      (error) => {
        setError(error.message)
      }
    )

    return () => {
      geolocationService.clearWatch(watchId)
    }
  }, [])

  return {
    position,
    loading,
    error,
    accuracy,
    getCurrentPosition,
    getCurrentPositionHighAccuracy,
    getCurrentPositionFast,
    watchPosition,
  }
}

export function useGeolocationData() {
  const [data, setData] = useState<GeolocationData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createGeolocation = async (geolocationData: any, file?: File) => {
    setLoading(true)
    setError(null)

    try {
      const result = await geolocationService.create(geolocationData, file)
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao criar geolocalização'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getGeolocations = async (page?: number, limit?: number, entityType?: string, entityId?: number) => {
    setLoading(true)
    setError(null)

    try {
      const result = await geolocationService.getAll(page, limit, entityType, entityId)
      setData(result.data || result)
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao buscar geolocalizações'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteGeolocation = async (id: number) => {
    setLoading(true)
    setError(null)

    try {
      await geolocationService.delete(id)
      // Atualizar a lista local removendo o item deletado
      setData(prev => prev.filter(item => item.id !== id))
      return true
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao deletar geolocalização'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getNearby = async (latitude: number, longitude: number, radius?: number) => {
    setLoading(true)
    setError(null)

    try {
      const result = await geolocationService.getNearby({
        latitude,
        longitude,
        radius,
      })
      setData(result)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar localizações próximas'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getByCity = async (city: string) => {
    setLoading(true)
    setError(null)

    try {
      const result = await geolocationService.getByCity(city)
      setData(result)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar localizações por cidade'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    data,
    loading,
    error,
    createGeolocation,
    getGeolocations,
    deleteGeolocation,
    getNearby,
    getByCity,
  }
}
